// Every string shown in an <Explain> popover, in one place.
//
// Why a keyed module instead of literals in JSX:
//
//   1. i18next is a dependency but not yet wired (see CLAUDE.md). When it
//      is, this file is the only thing that has to change — the keys here
//      are already the shape a translation catalogue wants. Literals
//      scattered across TavernPage would each be a separate migration.
//   2. Game copy is balance-adjacent. When reroll stops costing 1g, the
//      person changing gameConfig can grep one file instead of hunting
//      through components.
//
// House rules, enforced by explain.test.ts:
//   - `title` ≤ 4 words. It's a label, not a sentence.
//   - `body` ≤ 140 chars. Anything longer belongs in a card detail view,
//     not a popover on a 390px screen.
//   - No trailing whitespace, no double spaces.
//
// Anything dynamic (live cost, current tier, whether you can afford it)
// is passed as the `detail` prop at the call site rather than templated
// in here — `detail` renders as a separate muted line under `body`.

export interface ExplainCopy {
  title: string;
  body: string;
}

export const EXPLAIN = {
  // --- Tavern HUD ---------------------------------------------------
  'tavern.hp': {
    title: 'Your health',
    body: 'Lose a fight and the surviving enemy units chip this down. At zero your run is over.',
  },
  'tavern.gold': {
    title: 'Gold',
    body: 'Refills to round + 2 each turn, capped at 10. It does not carry over, so spend it before you end the turn.',
  },
  'tavern.round': {
    title: 'Round',
    body: 'Each round you get more gold, then your board fights one other crew in the lobby.',
  },
  'tavern.tier': {
    title: 'Tavern tier',
    body: 'Higher tiers put rarer, stronger units in your shop. Tier 1 only ever offers tier-1 cards.',
  },
  'tavern.placement': {
    title: 'Crews left',
    body: 'Eight crews start; each one that hits zero health is out. Where you finish is what the run is scored on.',
  },
  'tavern.boardPower': {
    title: 'Board power',
    body: 'Total attack and health across your board. A rough read on whether you out-stat the lobby.',
  },
  'tavern.timer': {
    title: 'Turn timer',
    body: 'The turn ends on its own when this runs out. It pauses while a discover or hero power is waiting on you.',
  },
  'tavern.hand': {
    title: 'Your hand',
    body: 'Cards you bought but have not played, up to 10. Drag one onto a board slot to field it.',
  },
  // No 'tavern.synergy' key: the header chip row is a button that opens the
  // SynergyPanel drawer, so tap belongs to the drawer, not to a popover.
  // The drawer explains itself inline.

  // --- Tavern actions -----------------------------------------------
  'tavern.reroll': {
    title: 'Reroll shop',
    body: 'Swaps the shop for a fresh lineup. Costs 1 gold, as often as you can afford it.',
  },
  'tavern.freeze': {
    title: 'Freeze shop',
    body: 'Keeps this exact lineup for next turn instead of rerolling it at the start of the round.',
  },
  'tavern.upgrade': {
    title: 'Upgrade tavern',
    body: 'Raises your tier so future shops offer stronger units. Gold spent now for a better board later.',
  },
  'tavern.scout': {
    title: 'Scout the lobby',
    body: 'Every crew still alive, with their hero, tier and board power. Your next opponent is rolled when you end the turn.',
  },
  'tavern.heroPower': {
    title: 'Hero power',
    body: 'Your hero’s signature move. Passive ones are always on; active ones can be used once per turn.',
  },
  'tavern.endTurn': {
    title: 'End turn',
    body: 'Locks your board in and sends it to fight. Any gold you did not spend is lost.',
  },
  'tavern.railCollapse': {
    title: 'Action rail',
    body: 'Collapse to icons to give the board more width, or expand for full labels and costs.',
  },
  // --- Staged, not yet wired ------------------------------------------
  // Authored with the rest so the voice is consistent and the copy rules
  // are already enforced, but the surfaces they belong to need Phase 2
  // (shared CardDetail + long-press on card tiles) and Phase 4 (hit-testing
  // on the combat canvas) before they have anywhere to attach. Wiring a
  // card tile means wrapping a rail item, which owns a load-bearing
  // height→aspect→width sizing chain — see the Rail module comment.
  'tavern.shop': {
    title: 'The shop',
    body: 'Five cards, rerolled each round. Buying one costs 3 gold and puts it in your hand.',
  },
  'tavern.board': {
    title: 'Your board',
    body: 'Seven slots. Combat resolves left to right, so the leftmost unit swings first.',
  },
  'tavern.sell': {
    title: 'Sell a unit',
    body: 'Drag a board unit onto the sell strip for +1 gold. The card is gone for good.',
  },
  'tavern.triple': {
    title: 'Triple ready',
    body: 'You hold two copies already. Buy this third one to fuse them into a golden unit with double stats.',
  },

  // --- Combat --------------------------------------------------------
  'combat.speed': {
    title: 'Playback speed',
    body: 'Only changes how fast the fight is drawn. The result is already decided and does not change.',
  },
  'combat.skip': {
    title: 'Skip playback',
    body: 'Jumps straight to the result. Nothing is forfeited — the fight has already been resolved.',
  },
  'combat.damage': {
    title: 'Damage taken',
    body: 'The winner deals damage equal to the total tier of their surviving units.',
  },
  'combat.blockReport': {
    title: 'Block report',
    body: 'How the other pairings in the lobby went this round, and who took damage.',
  },
  'combat.asyncLive': {
    title: 'Real opponent',
    body: 'A snapshot of a board another player actually built, not an AI crew.',
  },
} as const satisfies Record<string, ExplainCopy>;

export type ExplainId = keyof typeof EXPLAIN;

export function explainCopy(id: ExplainId): ExplainCopy {
  return EXPLAIN[id];
}
